import type { KnowledgeBaseCatalogue } from '@/lib/definitions/knowledgeBase'
import {
  knowledgeBaseCatalogueUrl,
  knowledgeBaseDocumentUrl,
} from '@/lib/misc/knowledgeBase'

export interface KnowledgeBaseFile {
  markdown: string
  lastModified?: string
}

export class KnowledgeBaseNotFoundError extends Error {
  constructor(path: string) {
    super(`Knowledge base document not found: ${path}`)
    this.name = 'KnowledgeBaseNotFoundError'
  }
}

export class KnowledgeBaseService {
  async getCatalogue(signal?: AbortSignal): Promise<KnowledgeBaseCatalogue> {
    const response = await fetch(knowledgeBaseCatalogueUrl(), {
      headers: { Accept: 'application/json' },
      signal,
    })
    if (!response.ok) {
      throw new Error(
        `Knowledge base catalogue request failed: ${response.status}`
      )
    }
    return response.json()
  }

  async getDocument(
    path: string,
    signal?: AbortSignal
  ): Promise<KnowledgeBaseFile> {
    const response = await fetch(knowledgeBaseDocumentUrl(path), {
      headers: { Accept: 'text/markdown, text/plain' },
      signal,
    })
    const contentType = response.headers.get('content-type') ?? ''
    if (
      response.status === 403 ||
      response.status === 404 ||
      contentType.includes('text/html')
    ) {
      throw new KnowledgeBaseNotFoundError(path)
    }
    if (!response.ok) {
      throw new Error(`Knowledge base request failed: ${response.status}`)
    }
    return {
      markdown: await response.text(),
      lastModified: response.headers.get('last-modified') ?? undefined,
    }
  }
}
